import { create } from "zustand";
import axios from "axios";

import kutil, { type PageInfo } from "@modules/kutil";
import config from "@modules/config";

export interface Menu {
  menuId: string,
  menuName: string,
  parentMenuId: string,
  menuUrl: string,
  displayYn: string,
  viewAuthYn: string,
  writeAuthYn: string,
  deleteAuthYn: string,
  downloadAuthYn: string,
  func1AuthYn: string,
  func2AuthYn: string,
  func3AuthYn: string,
  func4AuthYn: string,
  func5AuthYn: string,
  children: Menu[],
}

export type MenuMap = {
  [key: string]: Menu;
}

interface AuthStoreState {
  sessionId: string,
  userNo: string,
  empNo: string,
  userName: string,
  role: string,
  ip: string,
  loginTime: string,
  defWorkStationId: string,
  menuInfos: Menu[],
  menuInfosByMenuKey: MenuMap,
  init: () => Promise<boolean>;
  isLogin: () => boolean,
  logout: (logoutMsg?: string) => Promise<void>,
  getMenu: (path: string) => any;
  getAuth: (req: any) => any;
  getSessionId: () => string;
}

function removeUnauthMenu(menuInfos: Menu[]) {
  menuInfos.forEach(menuInfo => {
    if (menuInfo.children) {
      removeUnauthMenu(menuInfo.children);
    }
  });
  menuInfos.filter(menuInfo => menuInfo.viewAuthYn != "Y").forEach(menuInfo => {
    menuInfos.splice(menuInfos.findIndex(e => e.menuId == menuInfo.menuId), 1);
  });
}

function makeMenuInfos(resMenuInfos: Menu[]) {
  const menuInfos: Menu[] = [];
  const menuInfosByMenuId: MenuMap = {};
  const menuInfosByMenuKey: MenuMap = {};

  resMenuInfos.forEach(menuInfo => {
    menuInfosByMenuId[menuInfo.menuId] = kutil.copyObj<Menu, Partial<Menu>>(menuInfo, {children: []}) as Menu;

    const menuKey = menuInfo.menuUrl.replace(/_list$/, "").replace(/_detail$/, "");
    menuInfosByMenuKey[menuKey] = kutil.copyObj<Menu, Partial<Menu>>(menuInfo, {}) as Menu;
  });

  resMenuInfos.forEach(resMenuInfo => {
    if (resMenuInfo.displayYn != "Y") {
        return;
    }

    const menuInfo = menuInfosByMenuId[resMenuInfo.menuId];
    if (menuInfo.parentMenuId) {
      const parentMenuInfo = menuInfosByMenuId[menuInfo.parentMenuId];
      parentMenuInfo.children.push(menuInfo);
      parentMenuInfo.menuUrl = parentMenuInfo.children[0].menuUrl;
      if (menuInfo.viewAuthYn == "Y") {
        parentMenuInfo.viewAuthYn = "Y";
      }
    } else {
      menuInfos.push(menuInfo);
    }
  });

  removeUnauthMenu(menuInfos);
  return {menuInfos, menuInfosByMenuKey};
}

export const useAuthStore = create<AuthStoreState>((set, get) => ({
  sessionId: "",
  userNo: "",
  empNo: "",
  userName: "",
  role: "",
  ip: "",
  loginTime: "",
  defWorkStationId: "",
  menuInfos: [],
  menuInfosByMenuKey: {},
  init: async () => {
    try {
      let data;
      if (config.get("auth.useApigee") == "true") {
        data = await kutil.sendGet("/api/common/session.data?mode=search", {}, {skipLoading: true});
      } else {
        const params = new URLSearchParams(window.location.search);
        const empNo = params.get("empNo") || "";
        data = await kutil.sendGet("/api/common/session.data?mode=search&idToken=" + config.get("auth.idToken._" + empNo), {}, {skipLoading: true});
      }

      const session = data.session;
      const {menuInfos, menuInfosByMenuKey} = makeMenuInfos(data.menuInfos);
      set(prev => ({...prev,
        sessionId: session.sessionId,
        userNo: session.encUserNo,
        empNo: session.empNo,
        userName: session.userName,
        role: session.role,
        ip: session.ip,
        loginTime: session.loginTime,
        defWorkStationId: session.defWorkStationId || "",
        menuInfos: menuInfos,
        menuInfosByMenuKey: menuInfosByMenuKey,
      }));
      return true;
    } catch(e) {
      console.log(e);
      return false;
    }
  },
  isLogin: () => {
    return !!get().userNo;
  },
  logout: async (logoutMsg = "로그아웃되었습니다.") => {
    // 로그아웃처리
    try {
      await kutil.sendPost("/api/common/session.proc?mode=logout", {}, {skipLoading: true, skipLogout: true});
    } catch(_e) {
    }

    let callback = "";
    try {
      const res = await axios.post("/oauth2/v1/logout");
      callback = res?.data?.callback;
    } catch(_e) {
    }
    kutil.removeRefreshToken();
    await kutil.alert(logoutMsg);
    if (callback) {
      window.location.href = callback;
    } else {
      // 페이지 새로고침
      window.location.replace(window.location.href);
    }
  },
  getMenu: (path: string) => {
    const currMenuKey = path.replace(/_list$/, "").replace(/_detail$/, "");
    return get().menuInfosByMenuKey[currMenuKey];
  },
  getAuth: (req: any) => {
    const path = req.path || req;
    if (path.startsWith("/common/")) {
      return {
        view: true,
        write: true,
        delete: true,
        download: true,
        func1: true,
        func2: true,
        func3: true,
        func4: true,
        func5: true,
      };
    }

    const menu = get().getMenu(path);
    return {
      view: menu?.viewAuthYn == "Y",
      write: menu?.writeAuthYn == "Y",
      delete: menu?.deleteAuthYn == "Y",
      download: menu?.downloadAuthYn == "Y",
      func1: menu?.func1AuthYn == "Y",
      func2: menu?.func2AuthYn == "Y",
      func3: menu?.func3AuthYn == "Y",
      func4: menu?.func4AuthYn == "Y",
      func5: menu?.func5AuthYn == "Y",
    };
  },
  getSessionId: () => {
    return get().sessionId;
  }
}));

export const auth = (req: any) => {
  return useAuthStore.getState().getAuth(req);
};
